import React, { createContext, ReactNode, useContext, useReducer } from 'react'
import { isValidLocale, validLocale } from '../locale'
import appConfig from '../storage/config.storage'
import { IEvent } from '../types/IEvent'
import { TTabSelector } from '../types/TTabSelector'

interface IBaseContext {
    event?: IEvent
    currentTab?: TTabSelector
    currentAddsTab?: 'here' | 'around'
    showLangModal?: boolean
    lang?: validLocale
}

const language = navigator.language.split('-')[0]
const initialState: IBaseContext = {
    event: undefined,
    currentTab: 'home',
    currentAddsTab: 'here',
    showLangModal: false,
    lang: isValidLocale(language) ? language : 'fr'
}

// context reducer user to update context state for all components that use it (tabs, lang modal..)
function reducer(state: IBaseContext, action: { type: string; payload?: Record<string, any> }): IBaseContext {
    switch (action.type) {
        case 'set-event':
            return { ...state, event: action.payload?.event }
        case 'set-current-tab':
            return { ...state, currentTab: action.payload?.currentTab }
        case 'set-current-adds-tab':
            return { ...state, currentAddsTab: action.payload?.currentAddsTab }
        case 'set-show-lang-modal':
            return { ...state, showLangModal: action.payload?.showLangModal }
        case 'set-lang':
            return { ...state, lang: action.payload?.lang }
        default:
            throw new Error()
    }
}

// eslint-disable-next-line func-call-spacing
const BaseContext = createContext<[IBaseContext, (type: string, payload?: Record<string, any>) => void]>([
    initialState,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    () => {}
])
export const BaseProvider = ({ children }: { children: ReactNode }): JSX.Element => {
    const [data, dispatchData] = useReducer(reducer, initialState)

    return <BaseContext.Provider value={[data, (t, p) => dispatchData({ type: t, payload: p })]}>{children}</BaseContext.Provider>
}

export const useBase = () => {
    const [context, dispatch] = useContext(BaseContext)
    /**
     * Should ONLY be called once AND ONLY after IonicStorageModule is initialized
     */
    const initFromStorage = async () => {
        const eventConfig = await appConfig.getEvent()
        if (eventConfig) {
            dispatch('set-event', { event: eventConfig })
        }
    }
    const setEvent = (event: IEvent) => {
        dispatch('set-event', { event })
    }

    const setShowLangModal = (showLangModal: boolean) => {
        dispatch('set-show-lang-modal', { showLangModal })
    }
    const setLang = (lang: validLocale) => {
        dispatch('set-lang', { lang })
    }
    return {
        initFromStorage,
        event: context.event,
        setEvent,
        setShowLangModal,
        showLangModal: context.showLangModal,
        setLang,
        lang: context.lang
    }
}
export const useBaseAdds = () => {
    const [context, dispatch] = useContext(BaseContext)
    const setCurrentAddsTab = (currentAddsTab: 'here' | 'around') => {
        dispatch('set-current-adds-tab', { currentAddsTab })
    }
    return { currentAddsTab: context.currentAddsTab, setCurrentAddsTab }
}

export const useBaseTab = () => {
    const [context, dispatch] = useContext(BaseContext)
    const setCurrentTab = (currentTab: TTabSelector) => {
        dispatch('set-current-tab', { currentTab })
    }
    return { currentTab: context.currentTab, setCurrentTab }
}
